/**
 * Identitet trenutačne sesije za lokalne predmemorije i redove (SEC-01, SEC-02).
 *
 * Scope = `company_id:user_id:verzija-prava`. Verzija prava je sažetak uloge i
 * modula iz korisničkog objekta (backend nema zasebnu verziju prava).
 * Epoha se povećava pri svakoj promjeni sesije (prijava, odjava, druga osoba),
 * pa asinkroni posao započet u jednoj sesiji može provjeriti je li još važeći.
 */

export type ScopeUser = {
  id?: string | null;
  company_id?: string | null;
  role?: string | null;
  allowed_modules?: Record<string, boolean | undefined> | null;
  company?: { enabled_modules?: Record<string, boolean | undefined> | null } | null;
} | null | undefined;

export type ScopeSnapshot = { scope: string | null; epoch: number };

function flags(obj: Record<string, boolean | undefined> | null | undefined): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!obj) return out;
  for (const k of Object.keys(obj).sort()) {
    if (obj[k] !== undefined) out[k] = obj[k] === true;
  }
  return out;
}

function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

export function accessVersion(user: ScopeUser): string {
  return fnv1a(JSON.stringify([user?.role || '', flags(user?.allowed_modules), flags(user?.company?.enabled_modules)]));
}

export function scopeFor(user: ScopeUser): string | null {
  if (!user?.id) return null;
  return `${user.company_id || 'global'}:${user.id}:${accessVersion(user)}`;
}

let current: string | null = null;
let epoch = 0;

/** Poziva se sinkrono uz svaku promjenu tokena/korisnika (AuthContext, api 401). */
export function setSessionScope(user: ScopeUser): void {
  const next = scopeFor(user);
  if (next === current) return;
  current = next;
  epoch++;
}

/** Odjava ili nova prijava: i ista osoba dobiva novu epohu, pa stari odgovori ne vrijede. */
export function resetSessionScope(user: ScopeUser = null): void {
  current = scopeFor(user);
  epoch++;
}

export function getSessionScope(): string | null {
  return current;
}

export function scopeSnapshot(): ScopeSnapshot {
  return { scope: current, epoch };
}

export function isSnapshotCurrent(snap: ScopeSnapshot | null | undefined): boolean {
  return Boolean(snap && snap.scope && snap.scope === current && snap.epoch === epoch);
}

export class ScopeChangedError extends Error {
  constructor() {
    super('Sesija se promijenila; rezultat je odbačen.');
    this.name = 'ScopeChangedError';
  }
}
